import { byDimension, countOutcomes, currentOutcomes, passRate } from './aggregate'
import type { VocabularyRegistry } from './registry'
import type { DimensionStats, OutcomeCounts } from './aggregate'
import type { IndicatorOutcome, Report } from './types'

/**
 * Grouping software into projects.
 *
 * An assessment names the software it assessed and nothing above it, so the
 * project a piece of software belongs to has no source in the report. It comes
 * from a catalog that declares it, or from someone saying so here.
 *
 * Where nothing declares a project, software is left ungrouped rather than
 * placed in an invented one: a made-up grouping would be indistinguishable from
 * a real one a page later.
 */

export interface Project {
  id: string
  name: string
  description?: string
  /** Software identities, not display names. */
  members: string[]
  /** Declared by a catalog, or defined here. */
  origin: 'catalog' | 'user'
}

interface ProjectGroup {
  project: Project
  reports: Report[]
  /** Distinct software in this project, by identity. */
  software: Software[]
  outcomes: IndicatorOutcome[]
  counts: OutcomeCounts
  passRate: number | undefined
  dimensions: DimensionStats[]
}

export interface Grouping {
  groups: ProjectGroup[]
  /** Software belonging to no project. Shown as itself, not as a group. */
  ungrouped: Software[]
}

export interface Software {
  key: string
  /** The name the most recent assessment uses. */
  name: string
  url?: string
  /** Oldest first; an undated report sorts before every dated one. */
  reports: Report[]
  latest: Report
}

/**
 * Every distinct piece of software in a set of reports, keyed by identity.
 *
 * The one place reports are grouped by software, so every page agrees on which
 * run is the latest and therefore on what a project's figures are.
 */
export function softwareIn(reports: Report[]): Software[] {
  const byKey = new Map<string, Report[]>()
  for (const report of reports) {
    const existing = byKey.get(report.software.key)
    if (existing) existing.push(report)
    else byKey.set(report.software.key, [report])
  }
  return [...byKey.entries()]
    .map(([key, own]) => {
      own.sort((a, b) => (a.createdAt?.value ?? -Infinity) - (b.createdAt?.value ?? -Infinity))
      const latest = own[own.length - 1]!
      return { key, name: latest.software.name, url: latest.software.url, reports: own, latest }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

export function groupByProject(
  reports: Report[],
  projects: Project[],
  registry: VocabularyRegistry,
): Grouping {
  const software = softwareIn(reports)
  const claimed = new Set<string>()

  const groups = projects
    .map((project) => {
      const members = software.filter((entry) => project.members.includes(entry.key))
      for (const member of members) claimed.add(member.key)

      const own = members.flatMap((member) => member.reports)
      // Across a project, the same deduplication applies as anywhere else: the
      // most recent result per software, indicator and tool.
      const outcomes = currentOutcomes(own)
      const counts = countOutcomes(outcomes)

      return {
        project,
        reports: own,
        software: members,
        outcomes,
        counts,
        passRate: passRate(counts),
        dimensions: byDimension(outcomes, registry),
      }
    })
    // A project none of whose software is loaded is not shown; it is not empty,
    // it is simply not part of this session.
    .filter((group) => group.software.length > 0)
    .sort((a, b) => a.project.name.localeCompare(b.project.name))

  return {
    groups,
    ungrouped: software.filter((entry) => !claimed.has(entry.key)),
  }
}

export function newProject(name: string): Project {
  return {
    // Prefixed so a user-defined project can never collide with a declared one,
    // and randomised rather than timestamped: a clock only resolves to the
    // millisecond, so two projects created in quick succession shared an id,
    // which would have collided the moment anyone made two at once.
    id: `user:${name.trim().toLowerCase().replace(/\s+/g, '-')}-${randomSuffix()}`,
    name: name.trim(),
    members: [],
    origin: 'user',
  }
}

function randomSuffix(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 8)
  }
  return Math.random().toString(36).slice(2, 10)
}

/**
 * Moves software into a project, removing it from any other.
 *
 * Software belongs to one project. Allowing several would make every total
 * overlap, and unlike quality dimensions there is no reason in the data for it
 * to.
 */
export function assign(
  projects: Project[],
  softwareKey: string,
  projectId: string | null,
): Project[] {
  return projects.map((project) => {
    const without = project.members.filter((key) => key !== softwareKey)
    return project.id === projectId
      ? { ...project, members: [...without, softwareKey] }
      : { ...project, members: without }
  })
}
